import { useState } from "react";
import type React from "react";
import { twJoin } from "tailwind-merge";

import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { CheckVerified03Icon } from "@/components/Icons/CheckVerified03Icon";
import { Copy07Icon } from "@/components/Icons/Copy07Icon";
import { Download01Icon } from "@/components/Icons/Download01Icon";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { Button } from "@/design-system/Button";
import { cardSurfaceVariants } from "@/design-system/Card";
import { SectionCard } from "@/design-system/SectionCard";
import { Stack } from "@/design-system/Stack";
import { useTranslation } from "@/translations";

import type { PortfolioBulkUploadAddressesSubmissionSummary } from "./BulkUploadAddressesSubmissionResultView";

interface BulkUploadAddressesSubmissionSummaryViewProps {
  submissionSummary: PortfolioBulkUploadAddressesSubmissionSummary;
  onRestart: () => void;
}

export function BulkUploadAddressesSubmissionSummaryView({
  submissionSummary,
  onRestart,
}: BulkUploadAddressesSubmissionSummaryViewProps): React.ReactNode {
  const [submittedAt] = useState(() => new Date());
  const { t, i18n } = useTranslation();

  return (
    <>
      <PageGrid.Column size="100%">
        <SectionCard
          dataTestId="mass-bulk-upload-addresses-summary-section"
          icon={<CheckVerified03Icon />}
          title={t("page.portfolio-address-bulk-upload.section.import-summary.title")}
          description={t("page.portfolio-address-bulk-upload.section.import-summary.description", {
            datetime: formatDate(i18n, "datetime", submittedAt),
          })}
        >
          <Button onClick={onRestart}>{t("common.action.confirm")}</Button>
        </SectionCard>
      </PageGrid.Column>
      <PageGrid.Column size="100%">
        <h2 className="text-heading-2">{t("page.portfolio-address-bulk-upload.import-summary")}</h2>
      </PageGrid.Column>
      <PageGrid.Column size="50%">
        <article className={twJoin("flex h-full flex-col items-center gap-space-md p-space-md", cardSurfaceVariants())}>
          <Download01Icon size={24} />
          <Stack gap="sm">
            <h3 className="text-center text-heading-1">{submissionSummary.amountSuccess}</h3>
            <span>{t("page.portfolio-address-bulk-upload.section.import-summary.total-successful-imports")}</span>
          </Stack>
        </article>
      </PageGrid.Column>
      <PageGrid.Column size="50%">
        <article className={twJoin("flex h-full flex-col items-center gap-space-md p-space-md", cardSurfaceVariants())}>
          <Copy07Icon size={24} />
          <Stack gap="sm">
            <h3 className="text-center text-heading-1">{submissionSummary.amountDuplicates}</h3>
            <span>{t("page.portfolio-address-bulk-upload.section.import-summary.total-duplicate-skipped")}</span>
          </Stack>
        </article>
      </PageGrid.Column>
    </>
  );
}
